
export const calculateDistance = (
  lat1?: number | null,
  lon1?: number | null,
  lat2?: number | null,
  lon2?: number | null
): number | null => {
  // Check all coordinates are valid numbers
  if (
    lat1 === undefined || lat1 === null || isNaN(lat1) ||
    lon1 === undefined || lon1 === null || isNaN(lon1) ||
    lat2 === undefined || lat2 === null || isNaN(lat2) ||
    lon2 === undefined || lon2 === null || isNaN(lon2)
  ) {
    console.log('Invalid coordinates for distance calculation:', { lat1, lon1, lat2, lon2 });
    return null;
  }

  // Check for extreme values that might indicate bad data
  if (
    Math.abs(lat1) > 90 || Math.abs(lat2) > 90 ||
    Math.abs(lon1) > 180 || Math.abs(lon2) > 180
  ) {
    console.log('Coordinates out of range:', { lat1, lon1, lat2, lon2 });
    return null;
  }

  // Fast path for identical or very close coordinates (same location)
  if (Math.abs(lat1 - lat2) < 0.0001 && Math.abs(lon1 - lon2) < 0.0001) {
    // Return 0 for identical or extremely close coordinates
    return 0;
  }

  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  if (isNaN(distance)) {
    console.log('Distance calculation resulted in NaN:', { lat1, lon1, lat2, lon2 });
    return null;
  }
  
  // For consistency in comparison operations, round to 2 decimal places
  return Math.round(distance * 100) / 100;
};

const toRad = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

export const formatDistance = (distance: number | null): string => {
  if (distance === null) return 'Distance unavailable';
  
  // Handle edge cases with better formatting
  if (distance === 0) return 'Your location';
  
  // Very small distances (less than 0.1 miles) shown in feet
  if (distance > 0 && distance < 0.1) {
    const feet = Math.round(distance * 5280); // Convert miles to feet
    return `${feet} ft`;
  }
  
  // Medium distances (0.1 to 10 miles) shown with 1 decimal
  if (distance < 10) {
    return `${distance.toFixed(1)} mi`;
  }
  
  // Larger distances shown as whole numbers
  return `${Math.round(distance)} mi`;
};
